// A bulk Watchlist import (file upload or linked Excel) whose every row missed
// a gate publishes nothing: the server closes it as a successful outcome and
// the Watchlist stays as it was. That is a normal result, never a failure.
export const NOTHING_MATCHED_OUTCOME = 'nothing_matched';

const SKIPPED_REASON_LABELS = [
    {
        label: 'malformed identifiers',
        codes: ['missing_identifier_value', 'invalid_identifier_length', 'invalid_identifier_value'],
    },
    { label: 'unrecognised countries', codes: ['unknown_country_value'] },
    { label: 'invalid MICs', codes: ['invalid_mic_value'] },
    { label: 'duplicates', codes: ['duplicate_identifier_row'] },
];

const count = (value) => Math.max(0, Number(value) || 0);

const joinList = (items) => (
    items.length <= 1
        ? items.join('')
        : `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`
);

/** Whether a normalized Watchlist lifecycle is an import that matched nothing. */
export function isNothingMatched(lifecycle) {
    return lifecycle?.terminal_receipt?.publication_outcome === NOTHING_MATCHED_OUTCOME;
}

function skippedReasons(inputValidation) {
    const codeCounts = inputValidation?.error_code_counts;
    if (!codeCounts || typeof codeCounts !== 'object') return '';
    const labels = SKIPPED_REASON_LABELS
        .filter(({ codes }) => codes.some((code) => count(codeCounts[code]) > 0))
        .map(({ label }) => label);
    if (!labels.length) return '';
    const last = labels.pop();
    return ` (${labels.length ? `${labels.join(', ')} and ${last}` : last})`;
}

/**
 * "Nothing was added; your Watchlist is unchanged." followed by the rows by
 * reason: skipped by the file check, not identified, no market data, no
 * factor data. The counts are the import's input validation and the
 * operation's gate counters, as the server recorded them.
 */
export function nothingMatchedMessage({ progress = null, inputValidation = null } = {}) {
    const skipped = count(inputValidation?.total_error_count ?? inputValidation?.error_count);
    const unidentified = count(progress?.msi_terminal_count) - count(progress?.msi_success_count);
    const noMarketData = count(progress?.market_terminal_count) - count(progress?.market_success_count);
    const noFactorData = count(progress?.factor_terminal_count) - count(progress?.factor_success_count);
    const reasons = [
        skipped > 0
            ? `${skipped} ${skipped === 1 ? 'row was' : 'rows were'} skipped${skippedReasons(inputValidation)}`
            : '',
        unidentified > 0 ? `${unidentified} couldn't be identified` : '',
        noMarketData > 0 ? `${noMarketData} had no market data` : '',
        noFactorData > 0 ? `${noFactorData} had no factor data` : '',
    ].filter(Boolean);
    return [
        'Nothing was added; your Watchlist is unchanged.',
        reasons.length ? `${joinList(reasons)}.` : '',
    ].filter(Boolean).join(' ');
}
